import type { CategoryContent } from '../row.js';

/**
 * History & Civilisations, second chunk.
 *
 * Authored with the correct option first throughout; a build-time rotation
 * spreads the authored answer index across all four slots, so the position of
 * the answer here carries no information and needs none.
 */
export const MORE_HISTORY: CategoryContent = {
  msc: [
    [
      'The Golden Bull of 1356 regulated what?',
      [
        'The election of the Holy Roman Emperor by a fixed college of seven prince-electors',
        'The jurisdiction of imperial courts over the free cities of the Empire',
        'The right of the Empire\'s princes to coin money within their territories',
        'The obligations of imperial vassals to provide troops for Italian campaigns',
      ],
      0,
      'Charles IV fixed the electoral procedure and made it independent of papal confirmation. It held, essentially unamended, until 1806.',
    ],
    [
      'The Peace of Karlowitz (1699) is significant as what?',
      [
        'The first major Ottoman cession of European territory',
        'The treaty that first recognised Russian access to the Black Sea from the Ottomans',
        'The settlement that ended the War of the Holy League without territorial change',
        'The agreement that established permanent Ottoman embassies in European capitals',
      ],
      0,
      'Hungary and Transylvania passed to the Habsburgs. It reversed the direction of the frontier for the first time since the fourteenth century.',
    ],
    [
      'The Council of Troubles, remembered as the Blood Council, was established by whom?',
      [
        'The Duke of Alba, as governor-general in the Netherlands',
        'Philip II in person, during his single visit to the northern provinces',
        'Margaret of Parma, before her recall to Spain in 1567',
        'Cardinal Granvelle, in the course of the bishoprics reorganisation',
      ],
      0,
      'Set up in 1567 to try participants in the iconoclastic riots, and a reliable recruiting sergeant for the revolt it was meant to suppress.',
    ],
    [
      'The Corn Laws were repealed in which year?',
      ['1846', '1832', '1867', '1815'],
      0,
      'Peel split his own party to do it, in the middle of the Irish famine. 1815 is when the most restrictive version was imposed.',
    ],
    [
      'The Berlin Conference of 1884-85 established which principle?',
      [
        'That a claim to African territory required demonstrable effective occupation',
        'That existing African polities would be recognised as sovereign states',
        'That the Congo basin would be administered jointly by the participating powers',
        'That the Atlantic slave trade would be suppressed by a joint naval patrol',
      ],
      0,
      'It regulated competition between the European powers and involved no African representation. The Congo went to Leopold II personally, not to a condominium.',
    ],
    [
      'The Constitutions of Clarendon (1164) concerned what?',
      [
        'The boundary between ecclesiastical and royal jurisdiction in England',
        'The obligations of tenants-in-chief for military service to the crown',
        'The procedures for assessing and collecting the geld',
        'The rights of the forest and the penalties for their infringement',
      ],
      0,
      'Henry II\'s attempt to codify custom against clerical privilege. Becket\'s refusal to accept it is the proximate cause of his murder six years later.',
    ],
    [
      'The "first settlement" of 27 BC did what?',
      [
        'Formally restored the Republic while concentrating extraordinary powers in Augustus',
        'Abolished the Senate as a governing body in favour of an imperial council',
        'Created the office of emperor as a defined constitutional magistracy',
        'Divided the empire administratively between Augustus and Agrippa',
      ],
      0,
      'The fiction of restoration was the point: Augustus held no office that was formally new, which is why the transition is so hard to date.',
    ],
    [
      'The Tanzimat reforms were carried out by which state?',
      [
        'The Ottoman Empire',
        'Qajar Persia, under Nasir al-Din Shah',
        'Meiji Japan, in its first decade',
        'Muhammad Ali\'s Egypt, while still nominally Ottoman',
      ],
      0,
      'From 1839 to 1876: legal equality, a new commercial code, conscription and centralised taxation. Egypt reformed too, but that is a separate programme.',
    ],
    [
      'The Mfecane refers to what?',
      [
        'A period of upheaval, migration and state formation in southern Africa in the early nineteenth century',
        'The Zulu military reforms attributed to Shaka and their diffusion northward',
        'The frontier wars between the Cape Colony and the Xhosa polities',
        'The trekboer migration inland from the Cape into the highveld, and the frontier conflicts that followed it',
      ],
      0,
      'The Zulu reforms are one element within it. Whether it was principally caused by Zulu expansion, by slave-raiding from Delagoa Bay, or by drought is actively contested.',
    ],
    [
      'The Statute of Westminster 1931 did what?',
      [
        'Gave the Dominions legislative independence from the Westminster parliament',
        'Established the Commonwealth as a formal international organisation',
        'Transferred responsibility for Dominion foreign policy to London',
        'Created a right of appeal from Dominion courts to the Privy Council',
      ],
      0,
      'It enacted the Balfour Declaration of 1926. Ireland, Canada and South Africa adopted it immediately; Australia and New Zealand waited years.',
    ],
  ],
  phd: [
    [
      'The Investiture Controversy was settled by which agreement?',
      [
        'The Concordat of Worms, in 1122',
        'The Peace of Venice, in 1177',
        'The Concordat of Bologna, in 1516',
        'The Dictatus Papae, issued in 1075',
      ],
      0,
      'Bishops would be invested with spiritual office by the church and with temporal regalia by the emperor. The Dictatus Papae is Gregory VII opening the quarrel, not closing it.',
    ],
    [
      'The "military revolution" thesis is principally associated with whom?',
      [
        'Michael Roberts, later revised substantially by Geoffrey Parker',
        'Charles Tilly, in his work on coercion and capital',
        'Paul Kennedy, in his account of the rise and fall of great powers',
        'William McNeill, in his work on the pursuit of power',
      ],
      0,
      'Roberts located it in Sweden between 1560 and 1660; Parker stretched the chronology and shifted the emphasis to fortification and naval power.',
    ],
    [
      'The Ottoman devshirme was what?',
      [
        'A levy of Christian boys from the Balkans for military and administrative service',
        'A land-grant system supporting provincial cavalry in exchange for service',
        'The tax paid by non-Muslim subjects in place of military obligation',
        'The practice of rotating provincial governors to prevent local entrenchment',
      ],
      0,
      'It staffed both the janissaries and the highest offices of state. The land-grant system is the timar and the tax is the jizya.',
    ],
    [
      'The sixteenth-century Price Revolution is explained by which competing accounts?',
      [
        'Monetary expansion from American silver',
        'Debasement of coinage, against the collapse of Baltic grain exports',
        'Guild wage-setting, against the enclosure of common land',
        'Ottoman disruption of eastern trade, against the shift of routes to the Atlantic',
      ],
      0,
      'Hamilton\'s silver thesis versus the demographic case made by Postan and others. Relative prices - food rising fastest - are what the monetary account struggles with.',
    ],
    [
      'The Pragmatic Sanction of 1713 was intended to do what?',
      [
        'Keep the Habsburg lands undivided and allow female succession to them',
        'Guarantee the Protestant succession in the Holy Roman Empire',
        'Settle the partition of the Spanish inheritance among the claimants',
        'Fix the constitutional relationship between Hungary and the Habsburg crown',
      ],
      0,
      'Charles VI spent his reign buying recognition for it, and the powers repudiated it within months of his death anyway.',
    ],
    [
      'The Truce of Yam-Zapolsky (1582) ended fighting between Muscovy and which power?',
      [
        'Poland-Lithuania',
        'Sweden, which retained Narva under its terms',
        'The Livonian Order, then in dissolution',
        'The Crimean Khanate and its Ottoman patrons',
      ],
      0,
      'Ivan IV surrendered his Livonian claims to Bathory. The separate truce with Sweden, at Plussa, followed the next year.',
    ],
    [
      'The Diet of Speyer in 1529 produced what?',
      [
        'The formal protest from which the word Protestant derives',
        'The first imperial recognition of Lutheran territorial churches',
        'The confession of faith presented by Melanchthon to the emperor',
        'The suspension of the Edict of Worms across the Empire',
      ],
      0,
      'The minority protested at the revocation of the 1526 concession. The confession came at Augsburg the following year.',
    ],
    [
      'The Battle of Sekigahara in 1600 established what?',
      [
        'Tokugawa dominance over Japan',
        'The unification of Japan under Toyotomi Hideyoshi\'s regents',
        'The expulsion of Portuguese traders from the Japanese islands',
        'The end of the Onin War and the beginning of the Sengoku period',
      ],
      0,
      'Ieyasu took the title of shogun three years later. The Sengoku period begins with the Onin War in 1467, well over a century earlier.',
    ],
    [
      'The Sykes-Picot agreement of 1916 did what?',
      [
        'Divided the Ottoman Arab provinces into projected French and British spheres',
        'Promised an independent Arab kingdom in exchange for revolt against the Ottomans',
        'Established the mandate system under the League of Nations',
        'Partitioned Anatolia between Greece, Italy and France',
      ],
      0,
      'The Hussein-McMahon correspondence is the promise of Arab independence, and the incompatibility of the two is the substance of the grievance.',
    ],
    [
      'The Atlantic slave trade reached its peak volume in which century?',
      ['The eighteenth', 'The seventeenth', 'The nineteenth', 'The sixteenth'],
      0,
      'Roughly six million of the twelve and a half million embarked were carried in the eighteenth century. Volumes stayed high into the nineteenth despite abolition.',
    ],
  ],
  professor: [
    [
      'The Pirenne thesis argues what?',
      [
        'That Islamic expansion, not the Germanic invasions, broke Mediterranean unity and shifted Europe\'s centre north',
        'That the Roman economy had already collapsed before any invasion occurred',
        'That the Carolingian renaissance was continuous with late antique culture',
        'That urban life in the west survived unbroken into the eighth century, with no interruption of Mediterranean exchange',
      ],
      0,
      'Mohammed and Charlemagne, published posthumously in 1937. Archaeology has undercut the sharp break, but the question it framed has never been retired.',
    ],
    [
      'The "General Crisis" of the seventeenth century thesis is associated with whom, and how has it been recast?',
      [
        'Trevor-Roper and Hobsbawm, later recast around Little Ice Age climate by Parker',
        'Braudel and the Annales school, later recast around price series',
        'Wallerstein and world-systems theory, later recast around trade dependency',
        'North and Thomas, later recast around institutional property rights',
      ],
      0,
      'Global Crisis (2013) assembles the climatic case. The recasting matters because it makes a European debate a global one.',
    ],
    [
      'The Annales school is characterised by what emphasis?',
      [
        'Long-run structures and material life over the narrative of events',
        'The primacy of political narrative reconstructed from state archives',
        'Quantitative testing of hypotheses drawn from neoclassical economics',
        'The recovery of individual experience through close reading of trial records',
      ],
      0,
      'Bloch, Febvre and then Braudel\'s longue duree. Microhistory - Ginzburg, Le Roy Ladurie - is a partly reactive later development.',
    ],
    [
      'The Donation of Constantine was demonstrated to be a forgery by whom?',
      [
        'Lorenzo Valla, with Nicholas of Cusa reaching the same conclusion independently',
        'Erasmus, in the course of preparing his Greek New Testament',
        'Marsilius of Padua, in Defensor Pacis',
        'Petrarch, in his letters on the return of the papacy to Rome',
      ],
      0,
      'Valla\'s 1440 declamation is the classic demonstration of philological method turned on a political document.',
    ],
    [
      'The term "Columbian Exchange" was coined by whom?',
      [
        'Alfred Crosby',
        'William Cronon, in Changes in the Land',
        'Charles Mann, in 1493',
        'Jared Diamond, in Guns, Germs, and Steel',
      ],
      0,
      'Crosby, 1972. The other three are all downstream of it, which is a fair measure of how much work the concept did.',
    ],
    [
      'What did the "fiscal-military state" concept argue about eighteenth-century Britain?',
      [
        'That its power rested on tax capacity and public credit rather than on absolutist authority',
        'That naval supremacy alone explains its advantage over France',
        'That parliamentary sovereignty constrained war-making relative to its rivals',
        'That colonial trade revenues financed the wars without domestic taxation',
      ],
      0,
      'Brewer\'s Sinews of Power. The paradox it explains is a state with weak coercive apparatus extracting more revenue per head than the absolute monarchies.',
    ],
    [
      'Why does Carolingian minuscule matter for the transmission of classical texts?',
      [
        'It standardised a legible, word-separated script in which most surviving copies were made',
        'It was the first script to be written with a reed pen rather than a stylus',
        'It permitted the use of parchment in place of papyrus for long works',
        'It introduced the codex form in place of the scroll',
      ],
      0,
      'Most classical Latin survives through ninth-century copies. Renaissance humanists mistook the hand for Roman, which is why our roman type descends from it.',
    ],
    [
      'The "Great Divergence" debate concerns what?',
      [
        'When and why western European and Chinese economic trajectories separated',
        'Why industrialisation began in Britain rather than in the Low Countries',
        'The gap between colonial and metropolitan living standards after 1800',
        'The divergence of northern and southern European growth after the Black Death',
      ],
      0,
      'Pomeranz put the split late and located it in coal and colonies; the California school against Broadberry and Allen\'s earlier estimates is the live argument.',
    ],
    [
      'The Corpus Juris Civilis was compiled under whom, and comprises what?',
      [
        'Justinian - the Code, Digest, Institutes and later Novellae',
        'Theodosius II - the Code alone, superseding all prior imperial constitutions',
        'Diocletian - the Gregorian and Hermogenian collections',
        'Leo VI - the Basilika, restating earlier law in Greek',
      ],
      0,
      'The Digest is the part that mattered: its rediscovery at Bologna in the eleventh century is the foundation of the western civil law tradition.',
    ],
    [
      'The "Black Legend" refers to what?',
      [
        'A body of anti-Spanish writing emphasising colonial cruelty, much of it circulated by Spain\'s rivals',
        'The historiographical claim that Spanish decline began with Philip II',
        'The account of the Inquisition given by Protestant exiles from the Netherlands',
        'The tradition attributing the Armada\'s failure to divine intervention',
      ],
      0,
      'It draws heavily on Las Casas, who was Spanish and writing to reform Spanish practice - which is what makes the propaganda use of him complicated.',
    ],
  ],
};
